'use client'

import type { GraphQLResult } from 'aws-amplify/api'
import type { Product } from '@skop-app/lib-domain/types'
import { productAdapter } from '@skop-app/lib-domain/adapters'
import { SearchProductInput } from '@skop-app/lib-search'
import {
  type QueryFunctionContext,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query'

import {
  getProductStatusQuery,
  hydrateMapGQL,
  productByIDQuery,
  searchProductTotalQuery,
} from '@/services/graphql'
import { getAmplifyClient } from '@/services/amplifyClient'
import { listScopedProductStatuses } from '@/services/product/productStatus.list'
import { type ProductStatusesQueryKey } from '@/services/product/productStatuses.cache'
import { useUserContext } from '@/context'
import { resolveProjectPermissions } from '@/features/project/project.permissions'
import { useProject } from './projects'

const client = getAmplifyClient()

type ProjectInventoryStats = {
  activeTotal: number
  removedTotal: number
}

type ProjectInventoryBreakdownBucket = {
  key: string
  count: number
}

export type ProjectInventoryBreakdown = {
  activeCategoryItems: ProjectInventoryBreakdownBucket[]
  removedCategoryItems: ProjectInventoryBreakdownBucket[]
  actionItems: ProjectInventoryBreakdownBucket[]
}

type SearchProductStatusesProps = {
  props: QueryFunctionContext<ProductStatusesQueryKey, string | null>
  filters: SearchProductInput | undefined
}

export const searchProductStatuses = async ({
  props,
  filters,
}: SearchProductStatusesProps) =>
  listScopedProductStatuses({
    props,
    filters,
  })

const searchProductTotal = async (filter: SearchProductInput) => {
  try {
    const { data } = (await client.graphql({
      query: searchProductTotalQuery,
      variables: {
        filter,
      },
    })) as GraphQLResult<{
      searchProductStatusV2: {
        total?: number | null
      }
    }>

    const total = data?.searchProductStatusV2?.total
    if (total == null) throw new Error('project inventory total is unavailable')
    return total
  } catch (error) {
    console.error('error counting project products', error)
    throw error
  }
}

export const buildProjectScopedFilter = (
  projectID: string,
  filter: SearchProductInput,
  scope: SearchProductInput,
): SearchProductInput => {
  const projectScope = scope.projectID?.eq === projectID
  if (projectScope) {
    return { and: [{ projectID: { eq: projectID } }, filter] }
  }
  return { and: [scope, { projectID: { eq: projectID } }, filter] }
}

export const resolveProjectInventoryScope = ({
  project,
  userID,
  organizationMemberships,
  organizationScope,
  authorization,
  ownership,
  isOrganizationHydrationError,
}: {
  project:
    | {
        id?: string
        organizationID?: string | null
      }
    | null
    | undefined
  userID?: string
  organizationMemberships: Array<{
    value?: string
    status?: string
  }>
  organizationScope: {
    scopeKind: 'organization' | 'solo' | 'unresolved'
    activeOrganizationId?: string
    selectedOrganization?: { id?: string | null } | null
  }
  authorization?: Parameters<
    typeof resolveProjectPermissions
  >[0]['authorization']
  ownership?: Parameters<typeof resolveProjectPermissions>[0]['ownership']
  isOrganizationHydrationError: boolean
}): SearchProductInput | null => {
  if (
    !project ||
    !userID ||
    isOrganizationHydrationError ||
    organizationScope.scopeKind === 'unresolved'
  ) {
    return null
  }

  const organizationMembership = organizationMemberships.some(
    (membership) =>
      membership.value === project.organizationID &&
      membership.status === 'DONE',
  )

  const hasResolvedOrganization =
    organizationScope.scopeKind === 'organization' &&
    Boolean(
      organizationScope.activeOrganizationId &&
      organizationScope.selectedOrganization?.id ===
        organizationScope.activeOrganizationId,
    )
  const projectPermissions = resolveProjectPermissions({
    authorization,
    ownership,
    activeOrganizationId: organizationScope.activeOrganizationId,
    project,
  })

  if (
    hasResolvedOrganization &&
    organizationMembership &&
    project.organizationID === organizationScope.activeOrganizationId
  ) {
    return { organizationID: { eq: project.organizationID } }
  }

  // useProject has already authorized this exact project. External project
  // readers and confirmed SOLO users must keep that project-bound authority.
  if (
    organizationScope.scopeKind === 'solo' ||
    (hasResolvedOrganization &&
      (projectPermissions.isGuestAccount ||
        projectPermissions.isExternalProject))
  ) {
    return project.id ? { projectID: { eq: project.id } } : null
  }

  // No confirmed authority remains for this selected organization.
  return null
}

const ACTIVE_PROJECT_INVENTORY_FILTER: SearchProductInput = {
  type: { ne: 'done' },
  progress: { eq: 'active' },
  nature: { ne: 'service' },
}

const REMOVED_PROJECT_INVENTORY_FILTER: SearchProductInput = {
  type: { eq: 'done' },
  progress: { ne: 'done' },
  nature: { ne: 'service' },
}

const TRACKED_PROJECT_INVENTORY_FILTER: SearchProductInput = {
  nature: { ne: 'service' },
}

const getProjectInventoryStats = async (
  projectID: string,
  scope: SearchProductInput,
): Promise<ProjectInventoryStats> => {
  const [activeTotal, removedTotal] = await Promise.all([
    searchProductTotal(
      buildProjectScopedFilter(
        projectID,
        ACTIVE_PROJECT_INVENTORY_FILTER,
        scope,
      ),
    ),
    searchProductTotal(
      buildProjectScopedFilter(
        projectID,
        REMOVED_PROJECT_INVENTORY_FILTER,
        scope,
      ),
    ),
  ])

  return {
    activeTotal,
    removedTotal,
  }
}

export const useProjectInventoryStats = (projectID: string | undefined) => {
  const {
    data: project,
    isPending: isProjectPending,
    isError: isProjectError,
  } = useProject(projectID)
  const {
    ownership,
    authorization,
    organizationMemberships,
    organizationScope,
    isLoading,
    isLoadingTeam,
    isLoadingSelectedOrganization,
    isOrganizationHydrationError,
  } = useUserContext()
  const scope =
    isLoading ||
    isLoadingTeam ||
    isLoadingSelectedOrganization ||
    isOrganizationHydrationError
      ? null
      : resolveProjectInventoryScope({
          project,
          userID: ownership?.byUserID,
          organizationMemberships,
          organizationScope,
          authorization,
          ownership,
          isOrganizationHydrationError,
        })
  const query = useQuery({
    queryKey: ['projectInventoryStats', projectID, ownership?.byUserID, scope],
    queryFn: () => getProjectInventoryStats(projectID ?? '', scope!),
    enabled:
      !!projectID &&
      projectID !== 'create' &&
      !isProjectPending &&
      !isProjectError &&
      !!scope,
  })
  const isUnavailable =
    (Boolean(projectID && projectID !== 'create') &&
      !isProjectPending &&
      !isLoading &&
      !isLoadingTeam &&
      !isLoadingSelectedOrganization &&
      (isProjectError || isOrganizationHydrationError || !scope)) ||
    query.isError
  return {
    ...query,
    isUnavailable,
    isPending: query.isPending && !isUnavailable,
  }
}

const mapAggregateBuckets = (
  aggregateItems:
    | Array<{
        name: string
        result: {
          buckets: Array<{ key: string; doc_count: number }>
        }
      }>
    | undefined,
  aggregateName: string,
): ProjectInventoryBreakdownBucket[] => {
  const aggregate = aggregateItems?.find((item) => item.name === aggregateName)
  if (!aggregate || !Array.isArray(aggregate.result?.buckets)) {
    throw new Error(
      `project inventory aggregate ${aggregateName} is unavailable`,
    )
  }
  return aggregate.result.buckets.map((bucket) => ({
    key: bucket.key,
    count: bucket.doc_count,
  }))
}

const getProjectInventoryBreakdown = async (
  projectID: string,
  scope: SearchProductInput,
): Promise<ProjectInventoryBreakdown> => {
  try {
    const [activeResult, removedResult, actionResult] = await Promise.all([
      client.graphql({
        query: hydrateMapGQL,
        variables: {
          filter: buildProjectScopedFilter(
            projectID,
            ACTIVE_PROJECT_INVENTORY_FILTER,
            scope,
          ),
          aggregates: [{ field: 'category', name: 'category', type: 'terms' }],
        },
      }) as Promise<
        GraphQLResult<{
          searchProductStatusV2: {
            aggregateItems?: Array<{
              name: string
              result: {
                buckets: Array<{ key: string; doc_count: number }>
              }
            }>
          }
        }>
      >,
      client.graphql({
        query: hydrateMapGQL,
        variables: {
          filter: buildProjectScopedFilter(
            projectID,
            REMOVED_PROJECT_INVENTORY_FILTER,
            scope,
          ),
          aggregates: [{ field: 'category', name: 'category', type: 'terms' }],
        },
      }) as Promise<
        GraphQLResult<{
          searchProductStatusV2: {
            aggregateItems?: Array<{
              name: string
              result: {
                buckets: Array<{ key: string; doc_count: number }>
              }
            }>
          }
        }>
      >,
      client.graphql({
        query: hydrateMapGQL,
        variables: {
          filter: buildProjectScopedFilter(
            projectID,
            TRACKED_PROJECT_INVENTORY_FILTER,
            scope,
          ),
          aggregates: [{ field: 'action', name: 'action', type: 'terms' }],
        },
      }) as Promise<
        GraphQLResult<{
          searchProductStatusV2: {
            aggregateItems?: Array<{
              name: string
              result: {
                buckets: Array<{ key: string; doc_count: number }>
              }
            }>
          }
        }>
      >,
    ])

    if (
      !activeResult.data?.searchProductStatusV2 ||
      !removedResult.data?.searchProductStatusV2 ||
      !actionResult.data?.searchProductStatusV2
    ) {
      throw new Error('project inventory breakdown is unavailable')
    }

    return {
      activeCategoryItems: mapAggregateBuckets(
        activeResult.data?.searchProductStatusV2?.aggregateItems,
        'category',
      ),
      removedCategoryItems: mapAggregateBuckets(
        removedResult.data?.searchProductStatusV2?.aggregateItems,
        'category',
      ),
      actionItems: mapAggregateBuckets(
        actionResult.data?.searchProductStatusV2?.aggregateItems,
        'action',
      ),
    }
  } catch (error) {
    console.error('error aggregating project products', error)
    throw error
  }
}

export const useProjectInventoryBreakdown = (projectID: string | undefined) => {
  const {
    data: project,
    isPending: isProjectPending,
    isError: isProjectError,
  } = useProject(projectID)
  const {
    ownership,
    authorization,
    organizationMemberships,
    organizationScope,
    isLoading,
    isLoadingTeam,
    isLoadingSelectedOrganization,
    isOrganizationHydrationError,
  } = useUserContext()
  const scope =
    isLoading ||
    isLoadingTeam ||
    isLoadingSelectedOrganization ||
    isOrganizationHydrationError
      ? null
      : resolveProjectInventoryScope({
          project,
          userID: ownership?.byUserID,
          organizationMemberships,
          organizationScope,
          authorization,
          ownership,
          isOrganizationHydrationError,
        })
  const query = useQuery({
    queryKey: [
      'projectInventoryBreakdown',
      projectID,
      ownership?.byUserID,
      scope,
    ],
    queryFn: () => getProjectInventoryBreakdown(projectID ?? '', scope!),
    enabled:
      !!projectID &&
      projectID !== 'create' &&
      !isProjectPending &&
      !isProjectError &&
      !!scope,
  })
  const isUnavailable =
    (Boolean(projectID && projectID !== 'create') &&
      !isProjectPending &&
      !isLoading &&
      !isLoadingTeam &&
      !isLoadingSelectedOrganization &&
      (isProjectError || isOrganizationHydrationError || !scope)) ||
    query.isError
  return {
    ...query,
    isUnavailable,
    isPending: query.isPending && !isUnavailable,
  }
}

export const useProductStatus = (id: string | null) => {
  return useQuery({
    queryKey: ['productWithStatus', id],
    queryFn: () => getProductStatus(id ?? ''),
    enabled: !!id,
    select: (data) => productAdapter(data),
  })
}

// Raw version of product status for workflows needing backend shape (no adapter)
export const useProductStatusRaw = (id: string | null) => {
  return useQuery({
    queryKey: ['productWithStatus', id],
    queryFn: () => getProductStatus(id ?? ''),
    enabled: !!id,
  })
}

export const getProductStatus = async (
  id: string,
  options?: { requireComplete?: boolean },
) => {
  try {
    const response = (await client.graphql({
      query: getProductStatusQuery,
      variables: { id },
    })) as GraphQLResult<{ getProductStatus: Product }>

    if (options?.requireComplete && response.errors?.length) throw response
    return response.data.getProductStatus
  } catch (e) {
    console.error('error getting product', e)
    throw e
  }
}

/* Return a product with all its occurrences */
export const useProductByID = (productID: string) => {
  return useSuspenseQuery({
    queryKey: ['product', productID],
    queryFn: () => productByID(productID),
  })
}

export const productByID = async (productID: string) => {
  const { data } = (await client.graphql({
    query: productByIDQuery,
    variables: { productID },
  })) as GraphQLResult<{ productByID: { items: Product[] } }>
  return data?.productByID?.items
}
